import React, { useEffect, useState } from "react";
import ExportButton from "./ExportButton";
import { useListParams } from "hooks/useListParams";
import Pagination from "./common/Pagination";
import { FilterDrawer } from "./common/Drawer";
import ApiKeySelect from "./common/ApiKeySelect";
import { apiKeyLabel, useApiKeyNames } from "hooks/useApiKeyNames";
import { useParams } from "react-router-dom";
import { CommunicationService, EmailAddress } from "services/CommunicationService";
import { CustomerService } from "../services/CustomerService";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const PAGE_SIZE = 10;

const emptyFilters = { apiKeyId: '', emailAddresses: '', fullName: '', isBlocked: '', isSubscribed: '', blockReason: '', blockedFrom: '', blockedTo: '', createdFrom: '', createdTo: '' };

const CustomerEmailAddressesList: React.FC = () => {
  const { customerId: companyId } = useParams<{ customerId: string }>();

  const [emailAddresses, setEmailAddresses] = useState<EmailAddress[]>([]);
  const apiKeyNames = useApiKeyNames(emailAddresses.map((row) => row.apiKeyId));
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { pageNumber, setPageNumber, appliedFilters, setAppliedFilters } = useListParams(emptyFilters);
  // The API Key dropdown narrows the company-wide list to one key.
  const apiKeyId = appliedFilters.apiKeyId || undefined;
  const customerId = apiKeyId ? undefined : companyId;

  const [isExporting, setIsExporting] = useState(false);
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [selectedEmailAddress, setSelectedEmailAddress] = useState<{ address: string; isBlocked: boolean; apiKeyId: number } | null>(null);
  const [blockReason, setBlockReason] = useState("");

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState(emptyFilters);

  useEffect(() => {
    setTempFilters({ ...appliedFilters });
  }, [isFilterOpen, appliedFilters]);

  // The API key is picked from its own dropdown on the page, not the filter panel.
  const activeFilterCount = Object.entries(appliedFilters).filter(([key, v]) => key !== 'apiKeyId' && v !== '').length;

  useEffect(() => {
    if (!customerId) return;
    CustomerService.customer.getCustomer(parseInt(customerId)).catch(() => {});
  }, [customerId]);

  useEffect(() => {
    if (!customerId && !apiKeyId) return;
    const timer = setTimeout(async () => {
      setIsLoading(true);
      try {
        const response = await CommunicationService.emailAddresses.getEmailAddresses(
          customerId ? parseInt(customerId) : null,
          {
            pageNumber, pageSize: PAGE_SIZE, apiKeyId: apiKeyId ? parseInt(apiKeyId) : undefined,
            emailAddresses: appliedFilters.emailAddresses || undefined,
            fullName: appliedFilters.fullName || undefined,
            isBlocked: appliedFilters.isBlocked === 'true' ? true : appliedFilters.isBlocked === 'false' ? false : undefined,
            isSubscribed: appliedFilters.isSubscribed === 'true' ? true : appliedFilters.isSubscribed === 'false' ? false : undefined,
            blockReason: appliedFilters.blockReason || undefined,
            blockedFrom: appliedFilters.blockedFrom || undefined,
            blockedTo: appliedFilters.blockedTo || undefined,
            createdFrom: appliedFilters.createdFrom || undefined,
            createdTo: appliedFilters.createdTo || undefined,
          }
        );
        setEmailAddresses(response.data.emailAddresses || []);
        setTotalCount(response.data.totalCount || 0);
      } catch {
        setError("Failed to load email addresses");
      } finally {
        setIsLoading(false);
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [customerId, apiKeyId, appliedFilters, pageNumber]);

  const refetch = async () => {
    if (!customerId && !apiKeyId) return;
    setIsLoading(true);
    try {
      const response = await CommunicationService.emailAddresses.getEmailAddresses(
        customerId ? parseInt(customerId) : null,
        {
          pageNumber, pageSize: PAGE_SIZE, apiKeyId: apiKeyId ? parseInt(apiKeyId) : undefined,
          emailAddresses: appliedFilters.emailAddresses || undefined,
          fullName: appliedFilters.fullName || undefined,
          isBlocked: appliedFilters.isBlocked === 'true' ? true : appliedFilters.isBlocked === 'false' ? false : undefined,
          isSubscribed: appliedFilters.isSubscribed === 'true' ? true : appliedFilters.isSubscribed === 'false' ? false : undefined,
          blockReason: appliedFilters.blockReason || undefined,
          blockedFrom: appliedFilters.blockedFrom || undefined,
          blockedTo: appliedFilters.blockedTo || undefined,
          createdFrom: appliedFilters.createdFrom || undefined,
          createdTo: appliedFilters.createdTo || undefined,
        }
      );
      setEmailAddresses(response.data.emailAddresses || []);
      setTotalCount(response.data.totalCount || 0);
    } catch {
      setError("Failed to load email addresses");
    } finally {
      setIsLoading(false);
    }
  };

  const handleTempFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setTempFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...tempFilters });
    setIsFilterOpen(false);
  };

  const handleClearAllFilters = () => {
    setTempFilters({ ...emptyFilters, apiKeyId: appliedFilters.apiKeyId });
    setAppliedFilters({ ...emptyFilters, apiKeyId: appliedFilters.apiKeyId });
  };

  const handleBlockEmailAddress = async (address: string, shouldBlock: boolean, apiKeyId: number) => {
    try {
      const apiKey = await CustomerService.apiKey.getApiKeyValue(apiKeyId);
      await CommunicationService.emailAddresses.updateBlockStatus(
        {
          email: address,
          shouldBlock,
          reason: blockReason,
        },
        apiKey,
      );
      toast.success(`Email address ${shouldBlock ? "blocked" : "unblocked"} successfully`);
      setBlockModalOpen(false);
      setBlockReason("");
      setSelectedEmailAddress(null);
      refetch();
    } catch {
      toast.error(`Failed to ${shouldBlock ? "block" : "unblock"} email address`);
    }
  };

  const handleExport = async (format: string) => {
    if (!customerId && !apiKeyId) return;
    setIsExporting(true);
    try {
      const response = await CommunicationService.emailAddresses.exportEmailAddresses(
        customerId ? parseInt(customerId) : null,
        {
          apiKeyId: apiKeyId ? parseInt(apiKeyId) : undefined,
          emailAddresses: appliedFilters.emailAddresses || undefined,
          fullName: appliedFilters.fullName || undefined,
          isBlocked: appliedFilters.isBlocked === 'true' ? true : appliedFilters.isBlocked === 'false' ? false : undefined,
          isSubscribed: appliedFilters.isSubscribed === 'true' ? true : appliedFilters.isSubscribed === 'false' ? false : undefined,
          blockReason: appliedFilters.blockReason || undefined,
          blockedFrom: appliedFilters.blockedFrom || undefined,
          blockedTo: appliedFilters.blockedTo || undefined,
          createdFrom: appliedFilters.createdFrom || undefined,
          createdTo: appliedFilters.createdTo || undefined,
        },
        format,
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `email-addresses.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to export email addresses");
    } finally {
      setIsExporting(false);
    }
  };

  const openBlockModal = (address: string, isBlocked: boolean, apiKeyId: number) => {
    setSelectedEmailAddress({ address, isBlocked, apiKeyId });
    setBlockModalOpen(true);
  };

  if (error) {
    return <div className="alert alert-danger m-4">{error}</div>;
  }

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="fw-bold mb-1">Email Addresses</h3>
          <p className="text-muted mb-0">Manage and monitor email addresses</p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <ApiKeySelect
            id="email-addresses-api-key"
            customerId={companyId}
            value={appliedFilters.apiKeyId}
            onChange={(apiKeyId) => setAppliedFilters({ ...appliedFilters, apiKeyId })}
          />
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setIsFilterOpen(true)}
            disabled={isLoading}
          >
            <i className="bi bi-funnel-fill me-1"></i>
            Filter
            {activeFilterCount > 0 && (
              <span className="badge bg-primary ms-1">{activeFilterCount}</span>
            )}
          </button>
          <ExportButton onExport={handleExport} isExporting={isExporting} size="sm" />
          <button className="btn btn-outline-primary btn-sm" onClick={refetch} disabled={isLoading}>
            <i className={`${isLoading ? 'spinner-border spinner-border-sm' : 'bi bi-arrow-clockwise'} me-1`}></i>
            Refresh
          </button>
        </div>
      </div>

      <FilterDrawer
        title="Filter Email Addresses"
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onClear={handleClearAllFilters}
        onApply={handleApplyFilters}
      >
        <div className="mb-4">
          <label className="form-label fw-bold small text-muted">Email Address</label>
          <div className="input-group">
            <span className="input-group-text"><i className="bi bi-search"></i></span>
            <input
              type="text"
              className="form-control"
              placeholder="Search email addresses..."
              name="emailAddresses"
              value={tempFilters.emailAddresses}
              onChange={handleTempFilterChange}
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="form-label fw-bold small text-muted">Full Name</label>
          <div className="input-group">
            <span className="input-group-text"><i className="bi bi-search"></i></span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by full name..."
              name="fullName"
              value={tempFilters.fullName}
              onChange={handleTempFilterChange}
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="form-label fw-bold small text-muted">Blocked</label>
          <select
            className="form-select"
            name="isBlocked"
            value={tempFilters.isBlocked}
            onChange={handleTempFilterChange}
          >
            <option value="">All</option>
            <option value="true">Blocked</option>
            <option value="false">Not Blocked</option>
          </select>
        </div>

        <div className="mb-4">
          <label className="form-label fw-bold small text-muted">Subscribed</label>
          <select
            className="form-select"
            name="isSubscribed"
            value={tempFilters.isSubscribed}
            onChange={handleTempFilterChange}
          >
            <option value="">All</option>
            <option value="true">Subscribed</option>
            <option value="false">Unsubscribed</option>
          </select>
        </div>

        <div className="mb-4">
          <label className="form-label fw-bold small text-muted">Block Reason</label>
          <div className="input-group">
            <span className="input-group-text"><i className="bi bi-search"></i></span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by block reason..."
              name="blockReason"
              value={tempFilters.blockReason}
              onChange={handleTempFilterChange}
            />
          </div>
        </div>

        <hr className="my-3" />

        <div className="mb-4">
          <label className="form-label fw-bold small text-muted">Blocked From</label>
          <div className="input-group">
            <span className="input-group-text"><i className="bi bi-calendar-event"></i></span>
            <input
              type="date"
              className="form-control"
              name="blockedFrom"
              value={tempFilters.blockedFrom}
              onChange={handleTempFilterChange}
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="form-label fw-bold small text-muted">Blocked To</label>
          <div className="input-group">
            <span className="input-group-text"><i className="bi bi-calendar-event"></i></span>
            <input
              type="date"
              className="form-control"
              name="blockedTo"
              value={tempFilters.blockedTo}
              onChange={handleTempFilterChange}
            />
          </div>
        </div>

        <hr className="my-3" />

        <div className="mb-4">
          <label className="form-label fw-bold small text-muted">Created From</label>
          <div className="input-group">
            <span className="input-group-text"><i className="bi bi-calendar-event"></i></span>
            <input
              type="date"
              className="form-control"
              name="createdFrom"
              value={tempFilters.createdFrom}
              onChange={handleTempFilterChange}
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="form-label fw-bold small text-muted">Created To</label>
          <div className="input-group">
            <span className="input-group-text"><i className="bi bi-calendar-event"></i></span>
            <input
              type="date"
              className="form-control"
              name="createdTo"
              value={tempFilters.createdTo}
              onChange={handleTempFilterChange}
            />
          </div>
        </div>
      </FilterDrawer>

      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : emailAddresses.length === 0 ? (
        <div className="alert alert-info">
          {activeFilterCount > 0 ? "No email addresses match your search criteria." : "No email addresses found."}
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-striped table-hover">
              <thead>
                <tr>
                  <th>Email Address</th>
                  <th>Status</th>
                  <th>Block Reason</th>
                  <th>Blocked At</th>
                  <th>API Key</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {emailAddresses.map((email) => (
                  <tr key={email.address}>
                    <td>{email.address}</td>
                    <td>
                      <span className={`badge bg-${email.isBlocked ? "danger" : "success"}`}>
                        {email.isBlocked ? "Blocked" : "Active"}
                      </span>
                    </td>
                    <td>{email.blockReason || "-"}</td>
                    <td>{email.blockedAt ? new Date(email.blockedAt).toLocaleString() : "-"}</td>
                    <td className="text-nowrap">{apiKeyLabel(apiKeyNames, email.apiKeyId)}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => openBlockModal(email.address, email.isBlocked, email.apiKeyId)}
                        title={email.isBlocked ? `Blocked: ${email.blockReason || "No reason provided"}` : "Block Email Address"}
                      >
                        <i className={`bi bi-${email.isBlocked ? "unlock" : "lock"}`}></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination pageNumber={pageNumber} pageSize={PAGE_SIZE} totalCount={totalCount} onPageChange={setPageNumber} />
        </>
      )}

      {blockModalOpen && selectedEmailAddress && (
        <div className="modal fade show" style={{ display: "block" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {selectedEmailAddress.isBlocked ? "Unblock Email Address" : "Block Email Address"}
                </h5>
                <button type="button" className="btn-close" onClick={() => { setBlockModalOpen(false); setSelectedEmailAddress(null); setBlockReason(""); }}></button>
              </div>
              <div className="modal-body">
                {!selectedEmailAddress.isBlocked && (
                  <div className="mb-3">
                    <label htmlFor="blockReason" className="form-label">Reason for blocking</label>
                    <textarea
                      className="form-control"
                      id="blockReason"
                      rows={3}
                      value={blockReason}
                      onChange={(e) => setBlockReason(e.target.value)}
                      placeholder="Enter reason for blocking this email address"
                      required
                    />
                  </div>
                )}
                <p>
                  Are you sure you want to {selectedEmailAddress.isBlocked ? "unblock" : "block"} the email address{" "}
                  <strong>{selectedEmailAddress.address}</strong>?
                </p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setBlockModalOpen(false); setSelectedEmailAddress(null); setBlockReason(""); }}>
                  Cancel
                </button>
                <button
                  type="button"
                  className={`btn btn-${selectedEmailAddress.isBlocked ? "success" : "danger"}`}
                  onClick={() => handleBlockEmailAddress(selectedEmailAddress.address, !selectedEmailAddress.isBlocked, selectedEmailAddress.apiKeyId)}
                  disabled={!selectedEmailAddress.isBlocked && !blockReason.trim()}
                >
                  {selectedEmailAddress.isBlocked ? "Unblock" : "Block"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerEmailAddressesList;
